import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { bannersAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { resolveMediaUrl } from '../../lib/utils';

const ADSENSE_CLIENT = (process.env.REACT_APP_ADSENSE_CLIENT || '').trim();
const ADSENSE_SCRIPT_BASE = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
let adScriptPromise = null;
let adSenseUnlocked = false;
let adSenseListenersAttached = false;
const adSenseSubscribers = new Set();

function isLocalPreview() {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function unlockAdSense() {
  if (adSenseUnlocked) return;
  adSenseUnlocked = true;
  adSenseSubscribers.forEach((callback) => callback(true));
  adSenseSubscribers.clear();
}

function subscribeAdSenseUnlock(callback) {
  if (adSenseUnlocked || typeof window === 'undefined') {
    callback(true);
    return () => {};
  }

  adSenseSubscribers.add(callback);

  if (!adSenseListenersAttached) {
    adSenseListenersAttached = true;
    const options = { once: true, passive: true };
    window.addEventListener('scroll', unlockAdSense, options);
    window.addEventListener('pointerdown', unlockAdSense, options);
    window.addEventListener('touchstart', unlockAdSense, options);
    window.addEventListener('keydown', unlockAdSense, { once: true });
  }

  return () => {
    adSenseSubscribers.delete(callback);
  };
}

function scheduleIdle(callback, timeout = 1200) {
  if (typeof window === 'undefined') return () => {};
  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(callback, Math.min(timeout, 450));
  return () => window.clearTimeout(id);
}

function isAdSenseScriptForClient(script) {
  const src = String(script?.src || '');
  if (!src.includes('pagead2.googlesyndication.com/pagead/js/adsbygoogle.js')) return false;

  try {
    return new URL(src).searchParams.get('client') === ADSENSE_CLIENT;
  } catch (_) {
    const encodedClient = encodeURIComponent(ADSENSE_CLIENT);
    return src.includes(`client=${ADSENSE_CLIENT}`) || src.includes(`client=${encodedClient}`);
  }
}

function ensureAdSenseScript() {
  if (!ADSENSE_CLIENT || isLocalPreview() || typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(false);
  if (adScriptPromise) return adScriptPromise;
  adScriptPromise = new Promise((resolve) => {
    const existingScript = Array.from(document.scripts || []).find(isAdSenseScriptForClient);
    if (existingScript) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `${ADSENSE_SCRIPT_BASE}?client=${encodeURIComponent(ADSENSE_CLIENT)}`;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return adScriptPromise;
}

function buildBannerSlides(banners) {
  return (banners || []).flatMap((banner) => {
    const seenImages = new Set();
    const imagePool = [
      banner?.image,
      ...(Array.isArray(banner?.images) ? banner.images : []),
    ]
      .map(resolveMediaUrl)
      .filter(Boolean)
      .filter((image) => {
        if (seenImages.has(image)) return false;
        seenImages.add(image);
        return true;
      });

    return imagePool.map((image, index) => ({
      id: `${banner?.id || banner?.title || 'banner'}-${index}`,
      bannerId: banner?.id || '',
      image,
      title: banner?.title || '',
      url: banner?.url || '',
    }));
  });
}

function BannerSlideshow({ slides = [], onClick, onImpression, minHeight }) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const imageCount = slides.length;

  useEffect(() => {
    setCurrent(0);
  }, [imageCount]);

  const advance = useCallback(() => {
    if (!imageCount) return;
    setCurrent(c => (c + 1) % imageCount);
  }, [imageCount]);

  useEffect(() => {
    if (imageCount < 2) return () => {};
    timerRef.current = setInterval(advance, 4000);
    return () => clearInterval(timerRef.current);
  }, [advance, imageCount]);

  useEffect(() => {
    const currentSlide = slides[current];
    if (currentSlide?.bannerId) onImpression?.(currentSlide.bannerId);
  }, [current, onImpression, slides]);

  if (!imageCount) return null;

  const goTo = (i) => {
    setCurrent(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(advance, 4000);
  };

  const currentSlide = slides[current];
  const inner = (
    <div className="relative w-full overflow-hidden bg-[#fffaf4]" style={{ minHeight }}>
      {slides.map((slide, i) => (
        <img
          key={slide.id}
          src={slide.image}
          alt={slide.title || 'Banner'}
          className="w-full h-full object-cover absolute inset-0 transition-opacity duration-700"
          loading={i === current ? 'eager' : 'lazy'}
          decoding="async"
          style={{ opacity: i === current ? 1 : 0 }}
        />
      ))}
      {/* Dots */}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={e => { e.preventDefault(); e.stopPropagation(); goTo(i); }}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? 'bg-white w-3' : 'bg-white/50'}`}
          />
        ))}
      </div>
    </div>
  );

  if (currentSlide?.url) {
    return (
      <a
        href={currentSlide.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="block overflow-hidden"
        onClick={() => onClick?.(currentSlide.bannerId)}
      >
        {inner}
      </a>
    );
  }
  return <div className="block overflow-hidden">{inner}</div>;
}

function BannerImageFrame({ image, alt, minHeight }) {
  return (
    <div className="relative w-full overflow-hidden bg-[#fffaf4]" style={{ minHeight }}>
      <img
        src={image}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export default function AdSlot({ slot, fallbackSlot = '', adSenseSlot = '', className = '', minHeight = 90, title = '', hideForPremium = true }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adStatus, setAdStatus] = useState('');
  const [shouldLoad, setShouldLoad] = useState(false);
  const [allowAdSense, setAllowAdSense] = useState(adSenseUnlocked);
  const slotRef = useRef(null);
  const insRef = useRef(null);
  const adRendered = useRef(false);
  const trackedBanners = useRef(new Set());
  const isPremiumActive = user?.subscription_status === 'active';
  const shouldHideForPremium = hideForPremium && isPremiumActive;
  const displayTitle = title || t('article.advertising', 'Publicité');
  const adSenseEnabled = Boolean(adSenseSlot && ADSENSE_CLIENT && !isLocalPreview());

  useEffect(() => {
    if (shouldLoad || shouldHideForPremium) return () => {};
    const element = slotRef.current;
    if (!element) return () => {};

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return () => {};
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setShouldLoad(true);
      observer.disconnect();
    }, { rootMargin: '0px 0px' });

    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldHideForPremium, shouldLoad]);

  useEffect(() => {
    adRendered.current = false;
    setAdStatus('');
  }, [adSenseSlot, slot]);

  useEffect(() => {
    if (!adSenseEnabled || allowAdSense) return () => {};
    return subscribeAdSenseUnlock(setAllowAdSense);
  }, [adSenseEnabled, allowAdSense]);

  useEffect(() => {
    if (shouldHideForPremium) {
      setItems([]);
      setLoaded(true);
      return () => {};
    }
    if (!shouldLoad) {
      setItems([]);
      setLoaded(false);
      return () => {};
    }
    let cancelled = false;
    setLoaded(false);
    const slots = [slot, fallbackSlot].filter(Boolean);
    Promise.all(slots.map((currentSlot) => bannersAPI.list(currentSlot))).then((responses) => {
      if (cancelled) return;
      const seenIds = new Set();
      const mergedItems = responses.flatMap((response) => response.data || []).filter((item) => {
        const key = item?.id || `${item?.slot || ''}:${item?.title || ''}:${item?.image || ''}`;
        if (seenIds.has(key)) return false;
        seenIds.add(key);
        return true;
      });
      setItems(mergedItems);
    }).catch(() => {
      if (!cancelled) setItems([]);
    }).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [fallbackSlot, shouldHideForPremium, shouldLoad, slot]);

  const activeBanners = useMemo(() => (items || []).filter((item) => item?.is_active !== false), [items]);
  const banner = activeBanners[0] || null;
  const bannerSlides = useMemo(() => buildBannerSlides(activeBanners), [activeBanners]);

  const trackBannerImpression = useCallback((bannerId) => {
    if (!bannerId || trackedBanners.current.has(bannerId)) return;
    trackedBanners.current.add(bannerId);
    bannersAPI.impression(bannerId).catch(() => {});
  }, []);

  useEffect(() => {
    if (bannerSlides.length > 1 || !banner?.id) return;
    trackBannerImpression(banner.id);
  }, [banner, bannerSlides.length, trackBannerImpression]);

  useEffect(() => {
    if (!adSenseEnabled || !allowAdSense || !shouldLoad || banner || adRendered.current) return;
    let cancelled = false;
    setAdStatus('');
    const cancelIdle = scheduleIdle(() => {
      ensureAdSenseScript().then((ok) => {
        if (!ok || cancelled) return;
        try {
          if (window.adsbygoogle) {
            window.adsbygoogle.push({});
            adRendered.current = true;
          }
        } catch (_) {}
      });
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [adSenseEnabled, allowAdSense, banner, shouldLoad]);

  useEffect(() => {
    if (!adSenseEnabled || banner || !insRef.current || typeof MutationObserver === 'undefined') return () => {};
    const element = insRef.current;
    const syncStatus = () => setAdStatus(element.getAttribute('data-ad-status') || '');
    syncStatus();
    const observer = new MutationObserver(syncStatus);
    observer.observe(element, { attributes: true, attributeFilter: ['data-ad-status'] });
    return () => observer.disconnect();
  }, [adSenseEnabled, banner]);

  const handleBannerClick = (bannerId = banner?.id) => {
    if (!bannerId) return;
    bannersAPI.click(bannerId).catch(() => {});
  };

  if (shouldHideForPremium) return null;

  if (!shouldLoad) {
    return (
      <section ref={slotRef} className="space-y-3" data-testid={`ad-slot-${slot}`}>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b5e34] font-semibold">{displayTitle}</p>
        <div className="skeleton-shimmer rounded-[26px]" style={{ minHeight }} />
      </section>
    );
  }

  if (banner) {
    const bannerImage = bannerSlides[0]?.image || resolveMediaUrl(banner.image);
    const wrapperClass = `block overflow-hidden rounded-[26px] border border-[#ece3d4] bg-[#fffaf4] shadow-[0_18px_45px_rgba(15,23,42,0.10)] ${className}`.trim();

    return (
      <section ref={slotRef} className="space-y-3" data-testid={`ad-slot-${slot}`}>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b5e34] font-semibold">{displayTitle}</p>
        {bannerSlides.length > 1 ? (
          <div className={wrapperClass}>
            <BannerSlideshow
              slides={bannerSlides}
              onClick={handleBannerClick}
              onImpression={trackBannerImpression}
              minHeight={minHeight}
            />
          </div>
        ) : banner.url ? (
          <a href={banner.url} target="_blank" rel="noopener noreferrer sponsored" className={wrapperClass} onClick={handleBannerClick}>
            {bannerImage ? (
              <BannerImageFrame image={bannerImage} alt={banner.title || displayTitle} minHeight={minHeight} />
            ) : (
              <div className="flex items-center justify-center px-4 text-sm font-medium text-[#6b4b24]" style={{ minHeight }}>
                {banner.title || displayTitle}
              </div>
            )}
          </a>
        ) : (
          <div className={wrapperClass}>
            {bannerImage ? (
              <BannerImageFrame image={bannerImage} alt={banner.title || displayTitle} minHeight={minHeight} />
            ) : (
              <div className="flex items-center justify-center px-4 text-sm font-medium text-[#6b4b24]" style={{ minHeight }}>
                {banner.title || displayTitle}
              </div>
            )}
          </div>
        )}
      </section>
    );
  }

  if (adSenseEnabled && !allowAdSense) {
    return (
      <section ref={slotRef} className="space-y-3" data-testid={`ad-slot-${slot}`}>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b5e34] font-semibold">{displayTitle}</p>
        <div className="skeleton-shimmer rounded-[26px]" style={{ minHeight }} />
      </section>
    );
  }

  if (adSenseEnabled && adStatus !== 'unfilled') {
    return (
      <section ref={slotRef} className="space-y-3" data-testid={`ad-slot-${slot}`}>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b5e34] font-semibold">{displayTitle}</p>
        <ins
          ref={insRef}
          className="adsbygoogle block overflow-hidden rounded-[26px] border border-[#ece3d4] bg-[#fffaf4] shadow-[0_18px_45px_rgba(15,23,42,0.10)]"
          style={{ display: 'block', minHeight }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adSenseSlot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </section>
    );
  }

  if (!loaded) {
    return (
      <section ref={slotRef} className="space-y-3" data-testid={`ad-slot-${slot}`}>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b5e34] font-semibold">{displayTitle}</p>
        <div className="skeleton-shimmer rounded-[26px]" style={{ minHeight }} />
      </section>
    );
  }

  return null;
}
